import {
  FormControl,
  TextField,
  MenuItem,
  InputLabel,
  Select,
  Button,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { destinationTypeOptions } from "../../../constants/constantList";
import _, { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import SmartPasswordField from "../../../commonComponents/SmartPasswordField";
import { MoreHorizOutlined } from "@mui/icons-material";
import WorkflowSessionModal from "../../ItemProperties/WorkflowSessionModal";
import { decryptString, encryptString } from "../../../utils/utilFunctions";

const JMS = ({
  connectionDetails,
  handleBodyContent,
  screen,
  disableControl,
  direction,
  useConnection,
  repoPDC,
  source = "",
}) => {
  const shouldEncryptDecryptPassword = String(source || "").toLowerCase() !== "pdc";
  const { t } = useTranslation();
  const [connectionFactory, setConnectionFactory] = useState("");
  const [contextFactory, setContextFactory] = useState("");
  const [contextUrl, setContextUrl] = useState("");
  const [destinationType, setDestinationType] = useState("QUEUE");
  const [destinationName, setDestinationName] = useState("");
  const [durableClientId, setDurableClientId] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [content, setContent] = useState(null);
  const [workflowSessionModal, setWorkflowSessionModal] = useState({
    open: false,
    isFirstTime: false,
  });
  const [valueLabel, setValueLabel] = useState("");

  const destinationTypeDropDownItems = destinationTypeOptions.map(
    (obj, key) => (
      <MenuItem key={key} value={obj.value}>
        {obj.text}
      </MenuItem>
    )
  );

  const debouncedContentUpdate = debounce(handleBodyContent, 500);

  const onInputChange = (name, value) => {
    if(['GET_DATA', 'SEND_DATA'].includes(screen)){
      setContent((prev) => ({ add:{ ...prev?.add,  [name]: value} }));
      return;
    }
    setContent((prev) => {
      return { ...prev, [name]: value };
    });
  };

  useEffect(() => {
    if (!_.isEqual(content, connectionDetails)) debouncedContentUpdate(content);
  }, [content]);

  const workflowMappings = {
    [t("LBL_CONN_FACTORY")]: {
      stateSetter: setConnectionFactory,
      key: "CONNECTION_FACTORY_LOOKUP",
      value: connectionFactory,
    },
    [t("LBL_CONTEXT_FACTORY")]: {
      stateSetter: setContextFactory,
      key: "CTX_FACTORY",
      value: contextFactory,
    },
    [t("LBL_CONTEXT_URL")]: {
      stateSetter: setContextUrl,
      key: "INIT_URL",
      value: contextUrl,
    },
    [t("LBL_DESTINATION_NAME")]: {
      stateSetter: setDestinationName,
      key: "QUEUE_OR_TOPIC_NAME",
      value: destinationName,
    },
    [t("LBL_DURABLE_CLIENT_ID")]: {
      stateSetter: setDurableClientId,
      key: "DURABLE_SUBSCRIPTION_CLIENT_ID",
      value: durableClientId,
    },
    [t("LBL_USERNAME")]: {
      stateSetter: setUsername,
      key: "USERNAME",
      value: username,
    },
    [t("LBL_WS_PASSWORD")]: {
      stateSetter: setPassword,
      key: "PASSWORD",
      value: password,
    },
  };

  const valuesHandler = (value) => {
    if (workflowMappings[valueLabel]) {
      const { stateSetter, key } = workflowMappings[valueLabel];
      let encryptedValue = value;
      if (key === "PASSWORD") {
        encryptedValue = shouldEncryptDecryptPassword
          ? encryptString(value, process.env.REACT_APP_SECRET_KEY2)
          : value;
      }
      stateSetter(value);
      onInputChange(key, encryptedValue);
    }
  };
  
  const getFontWeight = (control, currentValue, connectionDetails, useConnection) => {
    if(control === "DESTINATION_NAME" && !repoPDC.hasOwnProperty("DESTINATION_NAME") && currentValue === "TOPIC") {
        return "normal"
    }
    return repoPDC?.[control] !== currentValue && useConnection === "preDefined"
      ? "bold"
      : "normal";
  }
  const fontWeights = useMemo(() => {
    return {
      CONNECTION_FACTORY: getFontWeight(
        "CONNECTION_FACTORY_LOOKUP",
        connectionFactory,
        connectionDetails,
        useConnection
      ),
      CONTEXT_FACTORY: getFontWeight(
        "CTX_FACTORY",
        contextFactory,
        connectionDetails,
        useConnection
      ),
      CONTEXT_URL: getFontWeight(
        "INIT_URL",
         contextUrl,
         connectionDetails,
         useConnection
      ),
      DESTINATION_TYPE: getFontWeight(
        "DESTINATION_TYPE",
        destinationType,
        connectionDetails,
        useConnection
      ),
      DESTINATION_NAME: getFontWeight(
        "QUEUE_OR_TOPIC_NAME",
        destinationName,
        connectionDetails,
        useConnection
      ),
      DURABLE_CLIENT_ID: getFontWeight(
        "DURABLE_SUBSCRIPTION_CLIENT_ID",
        durableClientId,
        connectionDetails,
        useConnection
      ),
      USERNAME: getFontWeight(
        "USERNAME",
        username,
        connectionDetails,
        useConnection
      ),
      PASSWORD: getFontWeight(
        "PASSWORD",
        password,
        connectionDetails,
        useConnection
      )

    }
  }, [
     useConnection,
     connectionDetails,
     connectionFactory,
     contextFactory,
     contextUrl,
     destinationName,
     destinationType,
     durableClientId,
     username,
     password
  ])

  useEffect(() => {
    setConnectionFactory(connectionDetails?.CONNECTION_FACTORY_LOOKUP || "");
    setContextFactory(connectionDetails?.CTX_FACTORY || "");
    setContextUrl(connectionDetails?.INIT_URL || "");
    setDestinationType(connectionDetails?.DESTINATION_TYPE || "QUEUE");
    setDestinationName(connectionDetails?.QUEUE_OR_TOPIC_NAME || "");
    setDurableClientId(connectionDetails?.DURABLE_SUBSCRIPTION_CLIENT_ID || "");
    setUsername(connectionDetails?.USERNAME || "");
    setPassword(
      connectionDetails?.PASSWORD
        ? shouldEncryptDecryptPassword
          ? decryptString(connectionDetails?.PASSWORD, process.env.REACT_APP_SECRET_KEY2)
          : connectionDetails?.PASSWORD
        : ''
    );
    setContent({ ...connectionDetails });
  }, [connectionDetails]);

  return (
    <>
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            label={t("LBL_CONN_FACTORY")}
            type="text"
            sx={{ input: { fontWeight: fontWeights.CONNECTION_FACTORY } }}
            value={connectionFactory}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setConnectionFactory(e.target.value);
            }}
            className={"width-100"}
            name={"CONNECTION_FACTORY_LOOKUP"}
            autoComplete="off"
          />
        </FormControl>
        {(screen === "SEND_DATA" || screen === "GET_DATA") && (
          <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
              setValueLabel(t("LBL_CONN_FACTORY"));
              setWorkflowSessionModal({
                open: true,
                isFirstTime: true,
              });
            }}>
            <MoreHorizOutlined fontSize="small" />
          </Button>
        )}
      </div>
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            label={t("LBL_CONTEXT_FACTORY")}
            type="text"
            value={contextFactory}
            sx={{ input: { fontWeight: fontWeights.CONTEXT_FACTORY } }}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setContextFactory(e.target.value);
            }}
            className={"width-100"}
            name={"CTX_FACTORY"}
            autoComplete="off"
          />
        </FormControl>
        {(screen === "SEND_DATA" || screen === "GET_DATA") && (
          <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
              setValueLabel(t("LBL_CONTEXT_FACTORY"));
              setWorkflowSessionModal({
                open: true,
                isFirstTime: true,
              });
            }}>
            <MoreHorizOutlined fontSize="small" />
          </Button>
        )}
      </div>
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            label={t("LBL_CONTEXT_URL")}
            type="text"
            value={contextUrl}
            sx={{ input: { fontWeight: fontWeights.CONTEXT_URL } }}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setContextUrl(e.target.value);
            }}
            className={"width-100"}
            name={"INIT_URL"}
            autoComplete="off"
          />
        </FormControl>
        {(screen === "SEND_DATA" || screen === "GET_DATA") && (
          <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
              setValueLabel(t("LBL_CONTEXT_URL"));
              setWorkflowSessionModal({
                open: true,
                isFirstTime: true,
              });
            }}>
            <MoreHorizOutlined fontSize="small" />
          </Button>
        )}
      </div>
      <FormControl variant="outlined" className="width-100" size="small">
        <InputLabel id="destinationTypeDropdownLabel">
          {t("LBL_DESTINATION_TYPE")}
        </InputLabel>
        <Select
          className={"width-100"}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
              },
            },
          }}
          name={"DESTINATION_TYPE"}
          labelId="destinationTypeDropdownLabel"
          label={t("LBL_DESTINATION_TYPE")}
          id="destinationTypeDropdown"
          value={destinationType || ""}
          sx={{ input: { fontWeight: fontWeights.DESTINATION_TYPE } }}
          onChange={(e) => {
            const value = e.target.value;
            onInputChange(e.target.name, value);
            setDestinationType(value);
            if (value === "QUEUE") {
              onInputChange("DURABLE_SUBSCRIPTION_CLIENT_ID", "");
              setDurableClientId("");
            }
          }}>
          {destinationTypeDropDownItems}
        </Select>
      </FormControl>
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            label={t("LBL_DESTINATION_NAME")}
            type="text"
            value={destinationName || ""}
            sx={{ input: { fontWeight: fontWeights.DESTINATION_NAME } }}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setDestinationName(e.target.value);
            }}
            className={"width-100"}
            name={"QUEUE_OR_TOPIC_NAME"}
            autoComplete="off"
          />
        </FormControl>
        {(screen === "SEND_DATA" || screen === "GET_DATA") && (
          <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
              setValueLabel(t("LBL_DESTINATION_NAME"));
              setWorkflowSessionModal({
                open: true,
                isFirstTime: true,
              });
            }}>
            <MoreHorizOutlined fontSize="small" />
          </Button>
        )}
      </div>
      {destinationType === "TOPIC" && (
        <div style={{ display: "flex", gap: "4px" }}>
          <FormControl className="width-100" size="small">
            <TextField
              variant="outlined"
              size="small"
              label={t("LBL_DURABLE_CLIENT_ID")}
              type="text"
              value={durableClientId || ""}
              sx={{ input: { fontWeight: fontWeights.DURABLE_CLIENT_ID } }}
              onChange={(e) => {
                onInputChange(e.target.name, e.target.value);
                setDurableClientId(e.target.value);
              }}
              className={"width-100"}
              name={"DURABLE_SUBSCRIPTION_CLIENT_ID"}
              autoComplete="off"
            />
          </FormControl>
          {(screen === "SEND_DATA" || screen === "GET_DATA") && (
            <Button
              size="small"
              disabled={disableControl || false}
              variant="outlined"
              onClick={() => {
                setValueLabel(t("LBL_DURABLE_CLIENT_ID"));
                setWorkflowSessionModal({
                  open: true,
                  isFirstTime: true,
                });
              }}>
              <MoreHorizOutlined fontSize="small" />
            </Button>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            label={t("LBL_USERNAME")}
            type="text"
            value={username || ""}
            sx={{ input: { fontWeight: fontWeights.USERNAME } }}
            onChange={(e) => {
              onInputChange("USERNAME", e.target.value);
              setUsername(e.target.value);
            }}
            className={"width-100"}
            name={"USER_NAME"}
            autoComplete="off"
          />
        </FormControl>
        {(screen === "SEND_DATA" || screen === "GET_DATA") && (
          <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
              setValueLabel(t("LBL_USERNAME"));
              setWorkflowSessionModal({
                open: true,
                isFirstTime: true,
              });
            }}>
            <MoreHorizOutlined fontSize="small" />
          </Button>
        )}
      </div>
      <div style={{ display: "flex", gap: "4px" }}>
        <SmartPasswordField
          variant="outlined"
          size="small"
          label={t("LBL_WS_PASSWORD")}
          value={password || ""}
          sx={{ input: { fontWeight: fontWeights.PASSWORD } }}
          onChange={(e) => {
            const encryptedValue = shouldEncryptDecryptPassword
              ? encryptString(e.target.value, process.env.REACT_APP_SECRET_KEY2)
              : e.target.value;
            onInputChange("PASSWORD", encryptedValue);
            setPassword(e.target.value);
          }}
          className={"width-100"}
          name={"PASS_WORD"}
          autoComplete="off"
        />
        {(screen === "SEND_DATA" || screen === "GET_DATA") && (
          <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
              setValueLabel(t("LBL_WS_PASSWORD"));
              setWorkflowSessionModal({
                open: true,
                isFirstTime: true,
              });
            }}>
            <MoreHorizOutlined fontSize="small" />
          </Button>
        )}
      </div>
      <WorkflowSessionModal
        valueLabel={valueLabel}
        showAccountData={true}
        showCallingWorkflow={true}
        showSystemData={true}
        showWorkflowSession={true}
        showDateAndTime={true}
        openModal={workflowSessionModal}
        setModalOpen={setWorkflowSessionModal}
        handleClose={() =>
          setWorkflowSessionModal({ open: false, isFirstTime: false })
        }
        initialValue={workflowMappings[valueLabel]?.value}
        valuesHandler={(value) => {
          valuesHandler(value);
        }}
        inputType={
          valueLabel === t("LBL_WS_PASSWORD") ||
          valueLabel === t("LBL_PASSWORD")
            ? "password"
            : "text"
        }
        specialPasswordField={
          valueLabel === t("LBL_WS_PASSWORD") ||
          valueLabel === t("LBL_PASSWORD")
        }
      />
    </>
  );
};

export default JMS;
